import React, { createContext, useContext, useState, useEffect } from 'react';
import { Status } from '../Model/Deployment';
import { AwsService } from '../Service/AwsService';

const DEFAULT_CONNECTED_MESSAGE = 'Connected to AWS';
const DEFAULT_DISCONNECTED_MESSAGE = 'Authenticating';

interface ConnectionStatusContextType {
  status: Status;
  updateStatus: (newStatus: Status) => void;
  setConnected: (message?: string) => void;
  setDisconnected: (message?: string) => void;
  checkConnectionStatus: () => Promise<void>;
  showAWSModal: boolean;
  setShowAWSModal: (show: boolean) => void;
}

export const ConnectionStatusContext = createContext<ConnectionStatusContextType>({
  status: { message: DEFAULT_DISCONNECTED_MESSAGE, success: false },
  updateStatus: () => {},
  setConnected: () => {},
  setDisconnected: () => {},
  checkConnectionStatus: async () => {},
  showAWSModal: false,
  setShowAWSModal: () => {},
});

interface ConnectionStatusProviderProps {
  children: React.ReactNode;
}

export const ConnectionStatusProvider: React.FC<ConnectionStatusProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<Status>({ 
    message: DEFAULT_DISCONNECTED_MESSAGE, 
    success: false 
  });
  const [showAWSModal, setShowAWSModal] = useState(false);
  const [isChecking, setIsChecking] = useState(false);


  const checkConnectionStatus = async () => {

    if (isChecking) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setDisconnected('Not authenticated');
        return;
      }

      // Update status to checking
      setStatus({ message: 'Checking AWS connection...', success: false });
      
      //const userRole = await AwsService.getRole();
      // if (userRole.success) {
      //   const verifyResult = await AwsService.verifyConnection(userRole.role_arn ?? '');
        
      //   // THIS IS THE KEY PART - Check API response
      //   if (verifyResult.success) {
      //     setConnected('AWS connection verified');
      //     setShowAWSModal(false); // Hide modal on success
      //   } else {
      //     // API returned success: false - Show the modal
      //     setDisconnected('AWS connection verification failed');
      //     setShowAWSModal(true); // Show modal when API returns false
      //   }
      // } else {
      //   setDisconnected('Failed to get AWS role');
      //   setShowAWSModal(true); // Show modal when getting role fails
      // }
    } catch (error) {
      setDisconnected('Error verifying AWS connection');
      setShowAWSModal(true); // Show modal on error
    }
  };

  // Check connection status on mount
  // useEffect(() => {
  //   checkConnectionStatus();
  // }, []);

  const updateStatus = (newStatus: Status) => {
    setStatus(newStatus);
  };

  const setConnected = (message: string = DEFAULT_CONNECTED_MESSAGE) => {
    setStatus({ message, success: true });
  };

  const setDisconnected = (message: string = DEFAULT_DISCONNECTED_MESSAGE) => {
    setStatus({ message, success: false });
  };

  const contextValue = {
    status,
    updateStatus,
    setConnected,
    setDisconnected,
    checkConnectionStatus,
    showAWSModal,
    setShowAWSModal,
  };

  return (
    <ConnectionStatusContext.Provider value={contextValue}>
      {children}
    </ConnectionStatusContext.Provider>
  );
};

export const useConnectionStatus = () => useContext(ConnectionStatusContext);

export function withConnectionStatus<P>(
  WrappedComponent: React.ComponentType<P & { connectionStatus: ConnectionStatusContextType }>
) {
  return function WithConnectionStatusComponent(props: P) {
    const connectionStatus = useContext(ConnectionStatusContext);
    return <WrappedComponent {...props} connectionStatus={connectionStatus} />;
  };
}